import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatCardModule } from '@angular/material/card';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ContractCheckService } from '../shared/contract-check.service';
import { ImportPromptSetsDialogComponent, ImportDialogData } from './import-dialog/import-prompt-sets-dialog.component';
import { PromptSet, PromptSetApiService } from '../shared/api.service';
import { Subscription } from 'rxjs';
import { DeleteConfirmDialogComponent } from './delete-confirm-dialog/delete-confirm-dialog.component';
import { ExportPreviewDialogComponent, ExportPreviewDialogData } from './export-preview-dialog/export-preview-dialog.component';
@Component({
    selector: 'app-admin-sidebar',
    standalone: true,
    imports: [
        MatDialogModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatListModule,
        MatCardModule,
        MatSnackBarModule,
        MatMenuModule,
        MatTooltipModule,
    ],
    templateUrl: './admin-sidebar.component.html',
    styleUrl: './admin-sidebar.component.scss'
})
export class AdminSidebarComponent implements OnInit, OnDestroy {
    contractChecks: PromptSet[] = [];
    selectedCheck: PromptSet | null = null;
    private refreshSubscription!: Subscription;

    constructor(
        private dialog: MatDialog,
        private fb: FormBuilder,
        private contractCheckService: ContractCheckService,
        private promptSetApiService: PromptSetApiService,
        private snackBar: MatSnackBar
    ) { }

    ngOnInit(): void {
        this.getListOfPromptSets();

        // Listen for refresh requests from other components (e.g. after delete all prompts)
        this.refreshSubscription = this.contractCheckService.refreshList$.subscribe((selectPromptSetId: string | null) => {
            this.getListOfPromptSets(selectPromptSetId);
        });
    }

    ngOnDestroy(): void {
        this.refreshSubscription?.unsubscribe();
    }

    openNewContractDialog(editItem?: any): void {
        const dialogRef = this.dialog.open(NewContractCheckDialog, {
            width: '550px',
            data: editItem // Pass the item being edited
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                if (editItem) {
                    // Update existing item
                    const index = this.contractChecks.findIndex(item => item.prompt_set_id === editItem.prompt_set_id);
                    if (index !== -1) {
                        this.contractChecks[index].name = result?.name;
                    }
                } else {
                    // Add new contract check and select it
                    const newCheck = {
                        prompt_set_id: result?.promptSetId || '',
                        name: result?.name || '',
                        selected: true,
                    };
                    this.contractChecks.push(newCheck);
                    this.onSelectCheck(newCheck);
                }
            }
        });
    }

    onEdit(item: any): void {
        this.openNewContractDialog(item);
    }

    onDelete(item: any): void {
        const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, {
            width: '300px',
            data: { prompt_set_id: item.prompt_set_id }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.getListOfPromptSets();
            }
        });
    }

    onSelectCheck(check: any) {
        this.selectedCheck = check;
        this.contractChecks.forEach(c => c.selected = c.prompt_set_id === check.prompt_set_id);
        
        // Normalize output_schema (snake_case) to outputSchema (camelCase) for component compatibility
        const normalizedCheck = { ...check };
        // Handle case where API returns output_schema instead of outputSchema
        if (normalizedCheck.output_schema && !normalizedCheck.outputSchema) {
            normalizedCheck.outputSchema = normalizedCheck.output_schema;
        }
        
        this.contractCheckService.setSelectedCheck(normalizedCheck);
    }

    getListOfPromptSets(selectPromptSetId?: string | null) {
        this.promptSetApiService.listPromptSets(undefined, true).subscribe((response: PromptSet[]) => {
            this.contractChecks = response.sort((a, b) => {
                const dateA = new Date(a.created_at || 0).getTime();
                const dateB = new Date(b.created_at || 0).getTime();
                return dateB - dateA; // Descending order (newest first)
            });
            
            if (this.contractChecks.length > 0) {
                // If a specific prompt set ID was requested, try to re-select it
                const targetCheck = selectPromptSetId
                    ? this.contractChecks.find(c => c.prompt_set_id === selectPromptSetId)
                    : null;
                this.onSelectCheck(targetCheck || this.contractChecks[0]);
            } else {
                this.selectedCheck = null;
                this.contractCheckService.setSelectedCheck(null);
            }
        });
    }

    openImportDialog(): void {
        const dialogRef = this.dialog.open(ImportPromptSetsDialogComponent, {
            width: '1200px',
        });

        dialogRef.afterClosed().subscribe((didImport: boolean) => {
            if (didImport) {
                this.getListOfPromptSets();
            }
        });
    }

    /**
     * Import prompts from Excel into an existing prompt set
     */
    importIntoPromptSet(check: PromptSet): void {
        const dialogData: ImportDialogData = { targetPromptSet: check };

        const dialogRef = this.dialog.open(ImportPromptSetsDialogComponent, {
            width: '1200px',
            data: dialogData
        });

        dialogRef.afterClosed().subscribe((didImport: boolean) => {
            if (didImport) {
                this.getListOfPromptSets();
            }
        });
    }

    /**
     * Export all prompt sets to Excel — opens a preview dialog first
     */
    exportToExcel(): void {
        if (this.contractChecks.length === 0) {
            this.snackBar.open('No prompt sets to export', 'Close', {
                duration: 3000,
                horizontalPosition: 'end',
                verticalPosition: 'top',
                panelClass: ['warning-snackbar']
            });
            return;
        }

        this.openExportPreviewDialog(this.contractChecks, 'all');
    }

    /**
     * Export selected prompt set to Excel — opens a preview dialog first
     */
    exportSelectedToExcel(): void {
        if (!this.selectedCheck) {
            this.snackBar.open('No prompt set selected for export', 'Close', {
                duration: 3000,
                horizontalPosition: 'end',
                verticalPosition: 'top',
                panelClass: ['warning-snackbar']
            });
            return;
        }

        this.openExportPreviewDialog([this.selectedCheck], 'selected');
    }

    /**
     * Open the export preview dialog showing stats and confirmation
     */
    private openExportPreviewDialog(promptSets: PromptSet[], mode: 'all' | 'selected'): void {
        const dialogData: ExportPreviewDialogData = { promptSets, mode };

        this.dialog.open(ExportPreviewDialogComponent, {
            width: '640px',
            data: dialogData
        });
    }
}

@Component({
    selector: 'new-contract-check-dialog',
    standalone: true,
    imports: [
        MatDialogModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
    ],
    templateUrl: './contract-check-dialog/new-contract-check-dialog.component.html',
    styleUrl: './contract-check-dialog/new-contract-check-dialog.component.scss'
})
export class NewContractCheckDialog implements OnInit {
    form: FormGroup;
    isEditMode: boolean = false;

    constructor(
        private fb: FormBuilder,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private promptSetApiService: PromptSetApiService,
        private dialogRef: MatDialogRef<NewContractCheckDialog>
    ) {
        this.isEditMode = !!data?.prompt_set_id;
        this.form = this.fb.group({
            name: [data?.name || '', [Validators.required, Validators.maxLength(100)]]
        });
    }

    onSubmit(): void {
        if (this.form.invalid) {
            return;
        }

        if (this.isEditMode) {
            this.updateContractCheck();
        } else {
            this.createContractCheck();
        }
    }

    createContractCheck(): void {
        this.promptSetApiService.createPromptSet({
            name: this.form.get('name')?.value
        }).subscribe((response: any) => {
            this.dialogRef.close(response);
        });
    }

    updateContractCheck(): void {
        const promptSetUpdater = {
            name: this.form.get('name')?.value
        };
        
        this.promptSetApiService.updatePromptSet({
            promptSetId: this.data.prompt_set_id,
            promptSetUpdater: promptSetUpdater
        }).subscribe((response: any) => {
            this.dialogRef.close(response);
        });
    }

    ngOnInit(): void { }
}
